/**
 * SEO Meta Tags Configuration
 * 
 * Centralized meta tag definitions for all pages
 * @module SEO-Meta-Tags
 */

export interface MetaData {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
}

// Default meta tags for the entire site
export const defaultMeta: MetaData = {
  title: "Anathoth Technologies - Leading IT Solutions & Software Development in Uganda",
  description: "Expert IT consulting, custom software development, mobile apps, and digital solutions in Uganda. We help businesses transform through technology with proven results.",
  keywords: "IT consulting Uganda, software development Uganda, mobile app development, web development, payment gateway integration, IT security Uganda, custom software, digital transformation",
  ogTitle: "Anathoth Technologies - Leading IT Solutions & Software Development in Uganda",
  ogDescription: "Expert IT consulting, custom software development, mobile apps, and digital solutions in Uganda. We help businesses transform through technology with proven results.",
  ogImage: "/assets/images/og-default.jpg",
  ogType: "website",
  canonicalUrl: "https://theanathoth.com"
};

// Page-specific meta tags
export const pageMeta: Record<string, MetaData> = {
  // Homepage
  "/": {
    title: "Anathoth Technologies - Expert IT Solutions & Software Development Uganda",
    description: "Leading IT consulting and software development company in Uganda. We build custom software, mobile apps, web platforms, and provide expert IT security solutions that drive business growth.",
    keywords: "IT consulting Uganda, software development Uganda, mobile app development, web development Uganda, payment gateway integration, IT security, custom software solutions, digital transformation Uganda",
    ogTitle: "Anathoth Technologies - Expert IT Solutions & Software Development Uganda",
    ogDescription: "Leading IT consulting and software development company in Uganda. We build custom software, mobile apps, web platforms, and provide expert IT security solutions.",
    canonicalUrl: "https://theanathoth.com"
  },

  // About Us
  "/about-us": {
    title: "About Anathoth Technologies - Uganda's Trusted IT Partner Since 2012",
    description: "Learn about Anathoth Technologies, Uganda's premier IT consulting and software development company. Our experienced team has delivered 200+ successful projects across East Africa.",
    keywords: "about Anathoth Technologies, IT company Uganda, software development team Uganda, IT consulting experience, technology partners Uganda",
    canonicalUrl: "https://theanathoth.com/about-us"
  },

  // Contact Us
  "/contact-us": {
    title: "Contact Anathoth Technologies - Get Your Free IT Consultation Today",
    description: "Ready to transform your business with technology? Contact Anathoth Technologies for expert IT consulting, software development, and digital solutions in Uganda. Free consultation available.",
    keywords: "contact Anathoth Technologies, IT consultation Uganda, software development quote, IT services Uganda, technology consultation",
    canonicalUrl: "https://theanathoth.com/contact-us"
  },

  // Our Team
  "/our-team": {
    title: "Meet Our Expert IT Team - Anathoth Technologies Uganda",
    description: "Meet the experienced professionals behind Anathoth Technologies. Our skilled team of developers, consultants, and IT experts deliver world-class technology solutions.",
    keywords: "Anathoth Technologies team, IT experts Uganda, software developers Uganda, IT consultants, technology team",
    canonicalUrl: "https://theanathoth.com/our-team"
  },

  // FAQ
  "/faq": {
    title: "Frequently Asked Questions - Anathoth Technologies IT Services",
    description: "Get answers to common questions about our IT consulting, software development, and digital transformation services. Learn about our processes, pricing, and expertise.",
    keywords: "IT services FAQ, software development questions, IT consulting FAQ Uganda, technology services questions",
    canonicalUrl: "https://theanathoth.com/faq"
  },

  // Services - IT Consultancy
  "/services/it-consultancy": {
    title: "Expert IT Consulting Services Uganda - Strategic Technology Planning",
    description: "Professional IT consulting services in Uganda. We help businesses make smart technology decisions, plan digital transformation, and avoid costly mistakes. 12+ years experience.",
    keywords: "IT consulting Uganda, technology strategy, IT advisory services, digital transformation consulting, technology planning Uganda, IT consultant",
    canonicalUrl: "https://theanathoth.com/services/it-consultancy"
  },

  // Services - Custom Software
  "/services/custom-software": {
    title: "Custom Software Development Uganda - Bespoke Business Solutions",
    description: "Professional custom software development and system integration services in Uganda. We build tailored software solutions that fit your exact business requirements.",
    keywords: "custom software development Uganda, bespoke software, system integration Uganda, business software solutions, enterprise software development",
    canonicalUrl: "https://theanathoth.com/services/custom-software"
  },

  // Services - Mobile Development
  "/services/mobile-development": {
    title: "Mobile App & Web Development Uganda - Professional Digital Solutions",
    description: "Expert mobile app and web development services in Uganda. We create user-friendly mobile applications and responsive websites that engage your customers effectively.",
    keywords: "mobile app development Uganda, web development Uganda, Android app development, iOS app development, responsive web design",
    canonicalUrl: "https://theanathoth.com/services/mobile-development"
  },

  // Services - Payment Gateway
  "/services/payment-gateway": {
    title: "Payment Gateway Integration Uganda - Secure Online Payment Solutions",
    description: "Professional payment gateway integration services in Uganda. We implement secure online payment systems, mobile money integration, and e-commerce payment solutions.",
    keywords: "payment gateway Uganda, mobile money integration, online payment systems Uganda, e-commerce payments, secure payment processing",
    canonicalUrl: "https://theanathoth.com/services/payment-gateway"
  },

  // Services - IT Security
  "/services/it-security": {
    title: "IT Security & Training Services Uganda - Protect Your Business",
    description: "Comprehensive IT security services and cyber security training in Uganda. We help businesses protect their data and train staff to recognize security threats.",
    keywords: "IT security Uganda, cyber security training, data protection Uganda, IT security consulting, security awareness training",
    canonicalUrl: "https://theanathoth.com/services/it-security"
  },

  // Success Stories
  "/success-stories": {
    title: "Success Stories - Anathoth Technologies Project Portfolio",
    description: "Explore our successful IT projects and case studies. See how we've helped businesses in Uganda and East Africa transform through custom software and digital solutions.",
    keywords: "IT projects Uganda, software development case studies, successful IT implementations, technology transformation stories",
    canonicalUrl: "https://theanathoth.com/success-stories"
  },

  // Blog
  "/blogs": {
    title: "IT Insights & Technology Blog - Anathoth Technologies",
    description: "Stay updated with the latest IT trends, technology insights, and digital transformation tips from Uganda's leading IT consulting and software development experts.",
    keywords: "IT blog Uganda, technology insights, software development tips, digital transformation blog, IT trends East Africa",
    canonicalUrl: "https://theanathoth.com/blogs"
  },

  // Policy Pages
  "/privacy-policy": {
    title: "Privacy Policy - Anathoth Technologies Data Protection",
    description: "Learn how Anathoth Technologies protects your personal information and privacy. Our comprehensive privacy policy outlines our data handling practices.",
    keywords: "privacy policy, data protection, Anathoth Technologies privacy, GDPR compliance",
    canonicalUrl: "https://theanathoth.com/privacy-policy"
  },

  "/terms-of-service": {
    title: "Terms of Service - Anathoth Technologies Legal Terms",
    description: "Review the terms and conditions for using Anathoth Technologies services. Understand our legal framework for IT consulting and software development services.",
    keywords: "terms of service, legal terms, service agreement, Anathoth Technologies terms",
    canonicalUrl: "https://theanathoth.com/terms-of-service"
  },

  "/cookie-policy": {
    title: "Cookie Policy - Anathoth Technologies Website Cookies",
    description: "Understand how Anathoth Technologies uses cookies on our website to improve your browsing experience and provide better services.",
    keywords: "cookie policy, website cookies, tracking policy, Anathoth Technologies cookies",
    canonicalUrl: "https://theanathoth.com/cookie-policy"
  },

  "/acceptable-use-policy": {
    title: "Acceptable Use Policy - Anathoth Technologies Service Guidelines",
    description: "Guidelines for the acceptable use of Anathoth Technologies services, systems, and platforms. Ensure proper usage of our IT solutions.",
    keywords: "acceptable use policy, service guidelines, IT service usage, Anathoth Technologies policies",
    canonicalUrl: "https://theanathoth.com/acceptable-use-policy"
  }
};

// Project-specific meta tags
export const projectMeta: Record<string, MetaData> = {
  "pel-church-platform": {
    title: "Church Platform - Complete Digital Ministry Solution | Anathoth Technologies",
    description: "Discover how we built PEL, a comprehensive church platform with live streaming, CRM, payment processing, and community engagement tools. 300% increase in online engagement.",
    keywords: "church platform, live streaming, church CRM, digital ministry, religious software, church management system",
    canonicalUrl: "https://theanathoth.com/success-stories/pel-church-platform"
  },

  "buildmate-construction": {
    title: "BuildMate Construction Platform - Project Management Solution | Anathoth Technologies",
    description: "Learn how we developed BuildMate, a specialized platform helping people in the diaspora to construct back home with ease.",
    keywords: "construction platform, project management, overseas construction, contractor network, construction technology",
    canonicalUrl: "https://theanathoth.com/success-stories/buildmate-construction"
  },

  "wot-tv-platform": {
    title: "WOT TV Platform - 24/7 Online Broadcasting Solution | Anathoth Technologies",
    description: "See how we created WOT TV, a sophisticated online television platform with LiquidSoap streaming, programming schedules, and 99.9% uptime for 50,000+ viewers.",
    keywords: "online TV platform, live broadcasting, streaming technology, media platform, television broadcasting",
    canonicalUrl: "https://theanathoth.com/success-stories/wot-tv-platform"
  },

  "school-of-divinity-lms": {
    title: "School of Divinity LMS - Advanced Learning Management System | Anathoth Technologies",
    description: "Explore our WordPress-based LMS for theological education with course monetization, student tracking, and 250% ROI for educational institutions.",
    keywords: "learning management system, LMS, online education, course monetization, educational technology, e-learning platform",
    canonicalUrl: "https://theanathoth.com/success-stories/school-of-divinity-lms"
  },

  "wip-prayer-platform": {
    title: "WIP Prayer Platform - Community Prayer Solution | Anathoth Technologies",
    description: "Discover our WebRTC-powered prayer platform that connects over 2,000 prayer warriors globally with real-time communication and community features.",
    keywords: "prayer platform, WebRTC, community platform, real-time communication, spiritual technology, prayer community",
    canonicalUrl: "https://theanathoth.com/success-stories/wip-prayer-platform"
  },

  "disciple-link-messaging": {
    title: "Disciple Link Messaging - Intelligent Bulk Messaging Solution | Anathoth Technologies",
    description: "Learn about our mobile app for personalized bulk messaging with carrier integration",
    keywords: "bulk messaging, mobile app, SMS automation, personalization, messaging platform, carrier integration",
    canonicalUrl: "https://theanathoth.com/success-stories/disciple-link-messaging"
  }
};

/**
 * Get meta data for a specific route
 * @param pathname - The current route pathname
 * @param projectId - Optional project ID for project detail pages
 * @returns MetaData object with appropriate meta tags
 */
export function getMetaForRoute(pathname: string, projectId?: string): MetaData {
  // Handle project detail pages
  if (pathname.startsWith('/success-stories/') && projectId && projectMeta[projectId]) {
    return {
      ...defaultMeta,
      ...projectMeta[projectId]
    };
  }

  // Handle regular pages
  if (pageMeta[pathname]) {
    return {
      ...defaultMeta,
      ...pageMeta[pathname]
    };
  }

  // Return default meta for unknown routes
  return defaultMeta;
}

/**
 * Generate structured data for the organization
 */
export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Anathoth Technologies Ltd",
  "alternateName": "Anathoth",
  "description": "Leading IT consulting and software development company in Uganda specializing in custom software, mobile apps, and digital transformation solutions.",
  "url": "https://theanathoth.com",
  "logo": "https://theanathoth.com/assets/images/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+256-744-666608",
    "contactType": "customer service",
    "email": "info@theanathoth.com",
    "availableLanguage": "English"
  },
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "UG",
    "addressLocality": "Uganda"
  },
  "sameAs": [
    "https://linkedin.com/company/anathoth-technologies",
    "https://twitter.com/anathothtech"
  ],
  "foundingDate": "2011",
  "numberOfEmployees": "10-50",
  "slogan": "Transforming businesses through innovative technology solutions"
}; 